/**
 * useSalary — OO «Зарплаты» ledger backbone (V1 screens_OO/290675–683).
 *
 * One endpoint does the heavy lifting: `/api/finance/summary?from&to` returns
 * the KPI block (revenue / FOT / discrepancies / reports) AND the full
 * per-operator ledger (`salary.staff[]`, each with per-day quests/mc/hall/
 * extra-work/penalties breakdown — computed by calculateSalary on the worker).
 * Period presets come from `/api/finance/salary-weeks` (Fri–Thu, UTC+5).
 *
 * The salary maths lives entirely on the backend (A16) — we only render it and
 * trigger the TG bulk-send (`/api/finance/notify-salary`).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

export type PeriodKey = "previous" | "current" | "month";

export interface SalaryRange { from: string; to: string; }
interface SalaryWeeks {
  current: SalaryRange;
  previous: SalaryRange;
  month: SalaryRange;
  today: string;
}

/** One earning line inside a day (a led game). */
export interface DayQuest {
  quest_name: string;
  short_name: string;
  time: string;
  players: number;
  rate: number;
  has_report: boolean;
  activity: string;
  comment: string;
  taxi_amount: number;
  zero_rate: boolean;
  /** game_report_edits count — >0 means a number was overridden by hand (A18). */
  edits: { count: number; editors: string } | null;
  /** ПР3 архива 1С: «Начислено (выплачено через 1С)» — в day_total и total не входит. */
  paid_1c?: boolean;
  /** ПР3 архива 1С: такси отчёта 1С — это «доп» из 1С, подпись «доп 1С». */
  taxi_1c?: boolean;
}
export interface DayMc { mc_name: string; quest_short: string; time: string; rate: number; paid_1c?: boolean; }
export interface DayHallAdmin {
  branch: string;
  hours: number;
  rate: number;
  earning: number;
  time_from: string;
  time_to: string;
  reported: boolean;
  comment: string;
}
export interface DayExtraWork {
  id: string;
  description: string;
  time_from: string;
  time_to: string;
  hourly_rate: number;
  rate_override: number | null;
  earning: number;
  taxi: number;
}
export interface DayPenalty { id: string | number; amount: number; reason: string; /** 26.10: особая отметка в расчётке (@ub/shared/slipMark) */ mark?: string | null }
/** Покупка оператора для компании (04.09, владелец): approved op_purchases —
 *  компенсация, НЕ зарплата, но выплачивается вместе с ней; цвет — lime. */
export interface DayPurchase { id: number; amount: number; description: string; comment?: string | null; created_at?: string }
/** Премия оператору (05.09, владелец): отправленные bonuses (status='sent') —
 *  плюс к зарплате по period_date; цвет везде — var(--blue). */
export interface DayBonus { id: number; amount: number; reason: string; issued_at?: string; /** 26.10: особая отметка в расчётке */ mark?: string | null }
export interface SalaryDay {
  quests: DayQuest[];
  mc: DayMc[];
  hall_admin: DayHallAdmin | null;
  extra_work?: DayExtraWork[];
  penalties?: DayPenalty[];
  purchases?: DayPurchase[];
  /** 05.09: премии (bonuses, status='sent') — плюс к day_total. */
  bonuses?: DayBonus[];
  day_total: number;
}
export interface OperatorSalary {
  staffId: string;
  name: string;
  quest_earnings: number;
  mc_earnings: number;
  hall_admin_earnings: number;
  extra_work_earnings: number;
  taxi_earnings: number;
  penalty_total: number;
  /** 04.09: компенсация покупок (approved op_purchases) — входит в total. */
  purchase_total: number;
  /** 05.09: премии (bonuses, status='sent') — входят в total. */
  bonus_total: number;
  total: number;
  games_count: number;
  /** ПР3 архива 1С: начислено и выплачено через 1С (игры 1С и всё до 01.03.2026) — НЕ в total. */
  paid_1c_total?: number;
  days: Record<string, SalaryDay>;
}

export interface SalaryKpi {
  revenue: number;
  cash: number;
  terminal: number;
  transfer: number;
  prepaid: number;
  fot: number;
  netto: number;
  reports: number;
  games: number;
  discrepancies: number;
  /** FOT breakdown for the ФОТ sub-line. */
  fotQuests: number;
  fotMc: number;
  fotHall: number;
  fotExtra: number;
  /** 04.09: компенсация покупок в периоде (breakdown.purchases). */
  fotPurchases: number;
  /** 05.09: премии в периоде (breakdown.bonuses). */
  fotBonuses: number;
}

interface SummaryResp {
  revenue?: {
    total_revenue?: number;
    total_cash?: number;
    total_terminal?: number;
    total_transfer?: number;
    total_prepaid?: number;
    report_count?: number;
  };
  noReport?: { count?: number };
  discrepancies?: { count?: number; total_diff?: number };
  salary?: {
    total?: number;
    breakdown?: { quests?: number; mc?: number; hall_admin?: number; extra_work?: number; purchases?: number; bonuses?: number };
    staff?: OperatorSalary[];
  };
}

export interface SalaryResult {
  period: PeriodKey;
  setPeriod: (p: PeriodKey) => void;
  range: SalaryRange | null;
  kpi: SalaryKpi | null;
  operators: OperatorSalary[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Bulk-send the period payslips to operators via TG. */
  notifySalary: () => Promise<{ ok: boolean; sent?: number; error?: string }>;
}

const EMPTY_KPI: SalaryKpi = {
  revenue: 0, cash: 0, terminal: 0, transfer: 0, prepaid: 0,
  fot: 0, netto: 0, reports: 0, games: 0, discrepancies: 0,
  fotQuests: 0, fotMc: 0, fotHall: 0, fotExtra: 0, fotPurchases: 0, fotBonuses: 0,
};

export function useSalary(): SalaryResult {
  const [weeks, setWeeks] = useState<SalaryWeeks | null>(null);
  const [period, setPeriod] = useState<PeriodKey>("previous");
  const [kpi, setKpi] = useState<SalaryKpi | null>(null);
  const [operators, setOperators] = useState<OperatorSalary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  const range: SalaryRange | null = weeks ? weeks[period] : null;

  // Load the period boundaries once (no auth required).
  useEffect(() => {
    let alive = true;
    api<SalaryWeeks>("/api/finance/salary-weeks")
      .then((w) => { if (alive) setWeeks(w); })
      .catch(() => { if (alive) setError("Не удалось получить периоды"); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!range) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);

    api<SummaryResp>(`/api/finance/summary?from=${range.from}&to=${range.to}`, { signal: ac.signal })
      .then((s) => {
        if (ac.signal.aborted) return;
        const rev = s.revenue ?? {};
        const sal = s.salary ?? {};
        const bd = sal.breakdown ?? {};
        const revenue = Number(rev.total_revenue) || 0;
        const fot = Number(sal.total) || 0;
        const reports = Number(rev.report_count) || 0;
        const noRep = Number(s.noReport?.count) || 0;
        setKpi({
          revenue,
          cash: Number(rev.total_cash) || 0,
          terminal: Number(rev.total_terminal) || 0,
          transfer: Number(rev.total_transfer) || 0,
          prepaid: Number(rev.total_prepaid) || 0,
          fot,
          netto: revenue - fot,
          reports,
          games: reports + noRep,
          discrepancies: Number(s.discrepancies?.count) || 0,
          fotQuests: Number(bd.quests) || 0,
          fotMc: Number(bd.mc) || 0,
          fotHall: Number(bd.hall_admin) || 0,
          fotExtra: Number(bd.extra_work) || 0,
          fotPurchases: Number(bd.purchases) || 0,
          fotBonuses: Number(bd.bonuses) || 0,
        });
        setOperators((sal.staff ?? []).slice().sort((a, b) => b.total - a.total));
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setKpi(EMPTY_KPI);
        setOperators([]);
        setError(e instanceof Error ? e.message : "Не удалось загрузить зарплаты");
        setLoading(false);
      });

    return () => ac.abort();
  }, [range?.from, range?.to, trigger]);

  const notifySalary = useCallback(async (): Promise<{ ok: boolean; sent?: number; error?: string }> => {
    if (!range) return { ok: false, error: "Период не выбран" };
    try {
      const res = await api<{ ok?: boolean; sent?: number; error?: string }>("/api/finance/notify-salary", {
        method: "POST",
        body: { from: range.from, to: range.to },
      });
      if (res?.error) return { ok: false, error: res.error };
      return { ok: true, sent: res?.sent ?? 0 };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : "Ошибка отправки" };
    }
  }, [range?.from, range?.to]);

  return { period, setPeriod, range, kpi, operators, loading, error, reload, notifySalary };
}
